import type { Ref } from 'vue'
import type { QueryParam } from '@app/application/user-management/utils/list-query'

import queryString from 'query-string'

import { onNextPageTeardown } from '@app/adapters/page-teardown'

export interface UrlField {
  key: string
  read: (param: QueryParam | null | undefined) => void
  serialize: () => string
  isInitial: () => boolean
}

interface ListHistory {
  push: () => void
  pushSearch: () => void
  replace: () => void
}

// Turbolinks restores an entry that carries its `turbolinks` key, and a restore
// swaps in a snapshot with no Vue app attached. An entry with only this key is
// one Turbolinks ignores, so the list handles it.
const STATE_KEY = 'userManagementList'
const LIST_STATE = { [STATE_KEY]: true }

let current: { pathname: string; read: () => void } | null = null

const isListEntry = (state: unknown): boolean =>
  typeof state === 'object' && state !== null && STATE_KEY in state

const onPopState = (event: PopStateEvent): void => {
  if (!isListEntry(event.state)) {
    return
  }

  // Turbo's listener runs first, because it registered first, and gives an
  // entry without a `turbo` key a state of its own. Putting the key back is how
  // the list still knows the entry the next time Back lands on it.
  window.history.replaceState(LIST_STATE, '')

  if (current?.pathname === window.location.pathname) {
    current.read()

    return
  }

  // An entry of a list page that is not the one on screen, reached from
  // another page. Nothing would render it, so it is loaded like a link.
  window.Turbolinks.visit(window.location.href, { action: 'replace' })
}

let listening = false

interface UrlFieldOptions<T> {
  key: string
  value: Ref<T>
  initial: T
  serialize: (value: T) => string
  deserialize: (param: QueryParam) => T
}

export const urlField = <T>({
  key,
  value,
  initial,
  serialize,
  deserialize,
}: UrlFieldOptions<T>): UrlField => ({
  key,
  // A param the URL does not carry is the default, so Back onto a URL without
  // it undoes what the user set.
  read: (param): void => {
    value.value = param ? deserialize(param) : initial
  },
  serialize: () => serialize(value.value),
  isInitial: () => value.value === initial,
})

/**
 * Keeps a list's query params in browser history. The list writes the URL once
 * per gesture: push for a state the user should step back to, replace for a
 * correction the user did not make.
 * @param fields - The params the list owns.
 * @returns The ways to write the URL.
 */
export const createListHistory = (fields: UrlField[]): ListHistory => {
  // The search box writes once per pause in typing, and one search should be
  // one Back press, so a search entry is rewritten until something else writes.
  let searchOnTop = false

  const read = (): void => {
    searchOnTop = false
    const params = queryString.parse(window.location.search)
    fields.forEach((field) => field.read(params[field.key]))
  }

  const write = (method: 'pushState' | 'replaceState') => (): boolean => {
    const params = queryString.parse(window.location.search)
    const before = queryString.stringify(params)

    fields.forEach((field) => {
      const next = field.serialize()

      // A param at its default stays out of a URL that did not carry it.
      if (next === '' || (field.isInitial() && !(field.key in params))) {
        delete params[field.key]
      } else {
        params[field.key] = next
      }
    })

    const search = queryString.stringify(params)

    // An entry for the view already on screen is one Back seems to skip.
    if (method === 'pushState' && search === before) {
      return false
    }

    window.history[method](
      LIST_STATE,
      '',
      `${search ? `?${search}` : window.location.pathname}${
        window.location.hash
      }`
    )

    return true
  }

  // Registered once for the document, not per component, so it outlives a
  // remount of the list. Registering it here rather than at import keeps it
  // behind Turbo's listener, which onPopState relies on.
  if (!listening) {
    window.addEventListener('popstate', onPopState)
    listening = true
  }

  read()

  // The entry the user arrived on becomes the list's too, so Back onto it is
  // read by the list rather than restored by Turbolinks.
  window.history.replaceState(LIST_STATE, '')

  const owner = { pathname: window.location.pathname, read }
  current = owner

  onNextPageTeardown(() => {
    if (current === owner) {
      current = null
    }
  })

  const push = write('pushState')
  const replace = write('replaceState')

  return {
    push: (): void => {
      if (push()) {
        searchOnTop = false
      }
    },
    pushSearch: (): void => {
      if (searchOnTop) {
        replace()
      } else {
        searchOnTop = push()
      }
    },
    replace: (): void => {
      replace()
    },
  }
}
